import { Fragment, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiChevronLeft, FiChevronRight, FiMapPin } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import isGHMC from "../../../Common/helper/helpar";
import {
  formatIeComplaintCreatedOn,
  formatIeComplaintDateTime,
  getIeComplaintCloseTimePosition,
} from "./ieComplaintUtils";
import IeComplaintActionHistoryTable from "./IeComplaintActionHistoryTable";

const FIGMA = {
  navy: "var(--color-primary)",
  orange: "#F5A623",
  textMuted: "#6B7280",
  expandBg: "#F1F5F9",
  subHeaderBg: "rgba(var(--color-primary-rgb), 0.04)",
  subHeaderText: "var(--color-primary)",
  border: "#eef0f3",
};

// Same look as the shared tables (theme.css): light tinted header with
// primary-colour text, white rows separated by a thin divider, no grid lines.
const HEAD_BG =
  "linear-gradient(rgba(var(--color-primary-rgb), 0.07), rgba(var(--color-primary-rgb), 0.07)), #ffffff";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const statusBadgeStyles = {
  Open: { color: "#9a6700", backgroundColor: "#fff3cd" },
  Pending: { color: "#0d6efd", backgroundColor: "#cfe2ff" },
  Closed: { color: "#0f5132", backgroundColor: "#d1e7dd" },
};

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  whiteSpace: "nowrap",
  border: "none",
  background: HEAD_BG,
  color: "var(--color-primary)",
  padding: "12px 14px",
  fontSize: "12.5px",
  fontWeight: 600,
  fontFamily: '"Poppins", sans-serif',
  textAlign: "center",
};

const tdStyle = {
  whiteSpace: "nowrap",
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  textAlign: "center",
  color: "#4b5565",
  fontSize: "13px",
  backgroundColor: "#FFFFFF",
};

const subThStyle = {
  whiteSpace: "nowrap",
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "left",
  backgroundColor: FIGMA.subHeaderBg,
  color: FIGMA.subHeaderText,
};

const subTdStyle = {
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  fontSize: "13px",
  textAlign: "left",
  color: "#4b5565",
  backgroundColor: "#FFFFFF",
};

const ExpandButton = ({ isExpanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={isExpanded ? "Collapse row" : "Expand row"}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18,
      height: 18,
      borderRadius: 3,
      border: `1px solid ${isExpanded ? FIGMA.orange : "#D1D5DB"}`,
      backgroundColor: "#FFFFFF",
      color: isExpanded ? FIGMA.orange : "#6B7280",
      fontSize: "12px",
      fontWeight: 500,
      cursor: "pointer",
      lineHeight: 1,
      padding: 0,
    }}
  >
    {isExpanded ? "−" : "+"}
  </button>
);

const IeComplaintExpandableTable = ({
  rows,
  onEdit,
  onFetchActionHistory,
  onViewCloseLocation,
  pagination,
  onPageChange,
  onPageSizeChange,
}) => {
  const isProd = isGHMC();
  const [expandedIds, setExpandedIds] = useState([]);
  const [localPage, setLocalPage] = useState(1);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(5);
  const [loadingHistoryIds, setLoadingHistoryIds] = useState([]);
  const [previewImage, setPreviewImage] = useState("");

  const isControlledPagination = Boolean(pagination);
  const currentPage = isControlledPagination ? pagination.currentPage : localPage;
  const rowsPerPage = isControlledPagination ? pagination.pageSize : localRowsPerPage;
  const totalResults = isControlledPagination
    ? pagination.totalCount ?? rows?.length ?? 0
    : rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalResults / rowsPerPage) || 1);

  // Always slice locally so page numbers match the full record total.
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return (rows ?? []).slice(start, start + rowsPerPage);
  }, [rows, currentPage, rowsPerPage]);

  const rowIdsKey = useMemo(
    () => (paginatedRows ?? []).map((row) => row.complaintId).join("|"),
    [paginatedRows]
  );

  const pageNumbers = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push(i);
    }
    return pages;
  }, [totalPages]);

  const rangeStart = totalResults ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const rangeEnd = Math.min(currentPage * rowsPerPage, totalResults);

  useEffect(() => {
    setExpandedIds([]);
    if (!isControlledPagination) {
      setLocalPage(1);
    }
  }, [rowIdsKey, isControlledPagination]);

  useEffect(() => {
    if (!isControlledPagination && localPage > totalPages) {
      setLocalPage(totalPages);
    }
  }, [localPage, totalPages, isControlledPagination]);

  const handlePageChange = (page) => {
    if (isControlledPagination) {
      onPageChange?.(page);
      return;
    }
    setLocalPage(page);
  };

  const handlePageSizeChange = (nextPageSize) => {
    if (isControlledPagination) {
      onPageSizeChange?.(nextPageSize);
      return;
    }
    setLocalRowsPerPage(nextPageSize);
    setLocalPage(1);
  };

  const toggleExpand = async (row) => {
    const rowId = row.complaintId;
    const isExpanded = expandedIds.includes(rowId);

    if (isExpanded) {
      setExpandedIds((prev) => prev.filter((id) => id !== rowId));
      return;
    }

    setExpandedIds((prev) => [...prev, rowId]);

    if (!row.actionHistory?.length && onFetchActionHistory) {
      setLoadingHistoryIds((prev) => [...prev, rowId]);
      try {
        await onFetchActionHistory(row.complaintId, row.complainantNo);
      } finally {
        setLoadingHistoryIds((prev) => prev.filter((id) => id !== rowId));
      }
    }
  };

  const handleViewCloseLocation = (row) => {
    if (!row || !onViewCloseLocation) return;

    const isOpenComplaint = row.status === "Open" || row.isClosed === false;
    if (isOpenComplaint) {
      toast.info("This complaint has not been closed yet.");
      return;
    }

    if (!getIeComplaintCloseTimePosition(row)) return;
    onViewCloseLocation(row);
  };

  return (
    <div>
      <div
        style={{
          overflowX: "auto",
          borderRadius: 16,
          border: `1px solid ${FIGMA.border}`,
          backgroundColor: "#FFFFFF",
          padding: "8px 10px 4px",
          boxShadow:
            "0 1px 2px rgba(17, 24, 39, 0.03), 0 6px 20px rgba(17, 24, 39, 0.05)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 2200 }}>
          <thead>
            <tr>
              <th style={thStyle}></th>
              <th style={thStyle}>S.No</th>
              <th style={thStyle}>Before Image</th>
              <th style={thStyle}>Complaint No</th>
              <th style={thStyle}>Complianant Contact No</th>
              <th style={thStyle}>KPI Category</th>
              <th style={thStyle}>Issue Type</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Zone</th>
              <th style={thStyle}>Ward </th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Complaint Created On</th>
              <th style={thStyle}>Complaint Closure On</th>
              <th style={thStyle}>Actual Duration (HH:MM)</th>
              <th style={thStyle}>Colony Name</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Mode Of Reporting</th>
              <th style={thStyle}>Close Time Location</th>
              <th style={thStyle}>Closing Image</th>
              <th style={thStyle}>Action Remarks</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!paginatedRows?.length ? (
              <tr>
                <td colSpan={21} style={{ ...tdStyle, padding: "24px" }}>
                  No data available
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, rowIndex) => {
                const serialNo = (currentPage - 1) * rowsPerPage + rowIndex + 1;
                const isExpanded = expandedIds.includes(row.complaintId);
                const isClosedComplaint =
                  row.status === "Closed" || row.isClosed === true;
                const isOpenComplaint = !isClosedComplaint;

                return (
                  <Fragment key={row.complaintId}>
                    <tr>
                      <td style={tdStyle}>
                        <ExpandButton
                          isExpanded={isExpanded}
                          onClick={() => toggleExpand(row)}
                        />
                      </td>
                      <td style={tdStyle}>{serialNo}</td>
                      <td style={tdStyle}>
                        {row.imageUrl ? (
                          <img
                            src={row.imageUrl}
                            alt="Before complaint"
                            onClick={() => setPreviewImage(row.imageUrl)}
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "cover",
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td style={tdStyle}>{row.complainantNo || "N/A"}</td>
                      <td style={tdStyle}>{row.contactNo || "N/A"}</td>
                      <td style={tdStyle}>{row.kpiCategory || "N/A"}</td>
                      <td style={tdStyle}>{row.issueType || "N/A"}</td>
                      <td style={tdStyle}>{row.description || "N/A"}</td>
                      <td style={tdStyle}>{row.zoneName || "N/A"}</td>
                      <td style={tdStyle}>{row.wardNo || "N/A"}</td>
                      <td style={tdStyle}>{row.location || "N/A"}</td>
                      <td style={tdStyle}>{formatIeComplaintCreatedOn(row.complaintCreatedOn)}</td>
                      <td style={tdStyle}>{formatIeComplaintDateTime(row.complaintClosureOn)}</td>
                      <td style={tdStyle}>{row.actualDuration || "N/A"}</td>
                      <td style={tdStyle}>{row.colonyName || "N/A"}</td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            ...(statusBadgeStyles[row.status] || statusBadgeStyles.Open),
                            padding: "4px 10px",
                            borderRadius: 4,
                            display: "inline-block",
                            minWidth: 72,
                          }}
                        >
                          {row.status || "N/A"}
                        </span>
                      </td>
                      <td style={tdStyle}>{row.modeOfReporting || "N/A"}</td>
                      <td style={tdStyle}>
                        {isOpenComplaint ? (
                          "N/A"
                        ) : getIeComplaintCloseTimePosition(row) ? (
                          <button
                            type="button"
                            title="View close time location on map"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleViewCloseLocation(row);
                            }}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#dc3545",
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              cursor: "pointer",
                              padding: 0,
                              fontSize: 14,
                            }}
                          >
                            <FiMapPin size={16} />
                            View
                          </button>
                        ) : (
                          row.closeTimeLocation || "N/A"
                        )}
                      </td>
                      <td style={tdStyle}>
                        {isOpenComplaint ? (
                          "N/A"
                        ) : row.closingImageUrl ? (
                          <img
                            src={row.closingImageUrl}
                            alt="Closing"
                            onClick={() => setPreviewImage(row.closingImageUrl)}
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "cover",
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td style={tdStyle}>
                        {isOpenComplaint ? "N/A" : row.actionRemarks || "N/A"}
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          className={isProd ? "edit-ghmc" : "edit-swm"}
                          onClick={() => onEdit(row)}
                          disabled={isClosedComplaint}
                          aria-label={`Edit ${row.complainantNo}`}
                          style={
                            isClosedComplaint
                              ? {
                                  opacity: 0.45,
                                  cursor: "not-allowed",
                                  pointerEvents: "none",
                                }
                              : undefined
                          }
                          title={
                            isClosedComplaint
                              ? "Edit is disabled for closed complaints"
                              : "Edit complaint"
                          }
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr style={{ backgroundColor: FIGMA.expandBg }}>
                        <td
                          colSpan={21}
                          style={{
                            border: `1px solid ${FIGMA.border}`,
                            padding: "16px 24px",
                          }}
                        >
                          <IeComplaintActionHistoryTable
                            history={row.actionHistory}
                            isLoading={loadingHistoryIds.includes(row.complaintId)}
                            subThStyle={subThStyle}
                            subTdStyle={subTdStyle}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: 16,
          paddingTop: 12,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: "14px", color: FIGMA.textMuted }}>
          Showing {rangeStart}-{rangeEnd} of {totalResults} results
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 20 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "14px",
              color: FIGMA.textMuted,
              margin: 0,
            }}
          >
            Row per page
            <select
              value={rowsPerPage}
              onChange={(event) => {
                handlePageSizeChange(Number(event.target.value));
              }}
              className="form-select form-select-sm"
              style={{ width: "auto" }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <nav style={{ display: "flex", alignItems: "center", gap: 6 }} aria-label="Pagination">
            <button
              type="button"
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.4 : 1,
              }}
              aria-label="Previous page"
            >
              <FiChevronLeft size={16} />
            </button>

            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => handlePageChange(page)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 32,
                  height: 32,
                  borderRadius: 6,
                  padding: "0 10px",
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  backgroundColor: currentPage === page ? FIGMA.navy : "#F0F4F8",
                  color: currentPage === page ? "#FFFFFF" : "#374151",
                }}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.4 : 1,
              }}
              aria-label="Next page"
            >
              <FiChevronRight size={16} />
            </button>
          </nav>
        </div>
      </div>

      <Modal show={!!previewImage} onHide={() => setPreviewImage("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={previewImage}
            alt="Preview"
            style={{ width: "100%", maxHeight: 400, objectFit: "contain", borderRadius: 5 }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default IeComplaintExpandableTable;
